import { useEffect, useState } from "react";
import { request } from "../../shared/api/client";
import { getStaffToken } from "../../shared/stores/staffSession";
import { hashParam } from "../../app/router/routes";
import type { GameId } from "../../shared/curriculum";
import { HOME_ART, HOME_BACKDROP, gameBackdrop, type Backdrop } from "./assets";
import { MARKETING_COPY, type StepCopy } from "./copy";
import { GAME_PAGE_COPY, type GamePageCopy } from "./games";

/**
 * The website's editable content (P3-21) — what an operator publishes from
 * `#/platform/website`, merged over the words this bundle already ships.
 *
 * 🔴 THE PUBLIC SITE NEVER DEPENDS ON THE API. Every page renders its built-in
 * copy first (`copy.ts`, `games.ts`); published content replaces it only after
 * it arrives inside 1.5 s AND passes this file's validator. A timeout, a 404
 * (nothing published), a 500, a malformed body — each one leaves the built-in
 * words on the page. A school buyer must never see an empty hero because the
 * CMS had a bad minute.
 *
 * 🔴 FIELD BY FIELD, NOT ALL OR NOTHING. A published body with one bad field
 * keeps every good field and falls back for that one, so a schema change on
 * the server (a field renamed, a limit tightened) degrades one sentence rather
 * than the page.
 *
 * ⚠️ THE VALIDATOR IS HAND-WRITTEN, AND IT IS THE SERVER'S ZOD SCHEMA AGAIN
 * (`backend/src/modules/website/site-content.schemas.ts`). The frontend has no
 * zod and its package.json is frozen; the two are held to identical
 * accept/reject answers by the shared fixtures in
 * `backend/src/modules/website/site-content.fixtures.json`, which both test
 * suites run. Change a rule here and there, or the parity tests fail.
 */

/* ── Keys ──────────────────────────────────────────────────────────────────── */

export const GAME_PAGE_KEYS = ["game.sky-quest", "game.turos-world", "game.letter-lagoon", "game.bridge-patterns"] as const;
export type GamePageKey = (typeof GAME_PAGE_KEYS)[number];

export const SITE_KEYS = [...GAME_PAGE_KEYS, "for-schools", "enquiry-form"] as const;
export type SiteKey = (typeof SITE_KEYS)[number];

/** The keys a marketing page renders from (the enquiry form is read by the contact screen, not a page). */
export type PageKey = GamePageKey | "for-schools";

export function isSiteKey(key: string): key is SiteKey {
  return (SITE_KEYS as readonly string[]).includes(key);
}

/** `#/sky-quest` → `game.sky-quest`; a game with no page of its own → null. */
export function gamePageKey(id: GameId): GamePageKey | null {
  const key = `game.${id}`;
  return (GAME_PAGE_KEYS as readonly string[]).includes(key) ? (key as GamePageKey) : null;
}

/* ── Media allow-lists (bundled art — D16) ─────────────────────────────────── */

export const HERO_BACKDROPS = ["home", "sky-quest", "turos-world", "letter-lagoon", "bridge-patterns"] as const;
export type HeroBackdropId = (typeof HERO_BACKDROPS)[number];

export const SCHOOLS_HERO_IMAGES = ["classroom", "home", "sunset"] as const;
export type SchoolsHeroImageId = (typeof SCHOOLS_HERO_IMAGES)[number];

/** The backdrop set an id names. Only bundled art: there is nothing else to choose. */
export function heroBackdrop(id: HeroBackdropId): Backdrop {
  return id === "home" ? HOME_BACKDROP : gameBackdrop(id);
}

/** The For schools hero still, wide and phone-sized. */
export function schoolsHeroImage(id: SchoolsHeroImageId): { src: string; small: string } {
  if (id === "home") return { src: HOME_BACKDROP.still, small: HOME_BACKDROP.stillSmall };
  if (id === "sunset") return { src: HOME_ART.sunset, small: HOME_ART.sunsetSmall };
  return { src: HOME_ART.classroomWide, small: HOME_ART.classroomWideSmall };
}

/* ── Enquiry form fields ───────────────────────────────────────────────────── */

export const ENQUIRY_FIELDS = [
  "schoolName",
  "board",
  "city",
  "state",
  "name",
  "designation",
  "email",
  "phone",
  "studentRange",
  "teacherRange",
  "startBy",
  "message",
] as const;
export type EnquiryField = (typeof ENQUIRY_FIELDS)[number];

/** Always required, whatever the setting — see the server schema. */
export const ENQUIRY_CORE_FIELDS = ["schoolName", "name", "email", "phone"] as const satisfies readonly EnquiryField[];

/* ── Content shapes ────────────────────────────────────────────────────────── */

export interface SeoCopy {
  title: string;
  description: string;
}

export interface GamePageContent extends GamePageCopy {
  heroMedia: HeroBackdropId;
  seo: SeoCopy;
}

export interface ForSchoolsContent {
  hero: {
    eyebrow: string;
    heading: string;
    sub: string;
    proof: [string, string, string];
    image: SchoolsHeroImageId;
    imageAlt: string;
  };
  gets: { label: string; heading: string; items: StepCopy[] };
  rollout: { label: string; heading: string; steps: [StepCopy, StepCopy, StepCopy, StepCopy] };
  sees: { label: string; heading: string; teacherCaption: string; parentCaption: string };
  games: { label: string; heading: string };
  finalHeading: string;
  seo: SeoCopy;
}

export interface EnquiryFormContent {
  requiredFields: EnquiryField[];
  consent: { version: string; text: string };
  autoReply: { enabled: boolean; subject: string; body: string };
  /** Operator-only; never in the public view. */
  alertRecipients: string[];
}

/** What `GET /site/content/enquiry-form` returns — no addresses, no auto-reply. */
export type EnquiryFormPublic = Pick<EnquiryFormContent, "requiredFields" | "consent">;

export interface SiteContentMap {
  "game.sky-quest": GamePageContent;
  "game.turos-world": GamePageContent;
  "game.letter-lagoon": GamePageContent;
  "game.bridge-patterns": GamePageContent;
  "for-schools": ForSchoolsContent;
  "enquiry-form": EnquiryFormContent;
}

/* ── Text rules (identical to the server's) ────────────────────────────────── */

const TAG_LIKE = /<[A-Za-z!/?]/;

/** A C0 control character other than a line feed, or DEL. */
export function hasControlChar(s: string): boolean {
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if ((c < 0x20 && c !== 0x0a) || c === 0x7f) return true;
  }
  return false;
}

/** Bounded, not blank, no markup, no control characters. */
export function isPlainText(s: string, max: number): boolean {
  return s.length <= max && s.trim().length > 0 && !TAG_LIKE.test(s) && !hasControlChar(s);
}

const EMAIL = /^[^\s@<>()",;:]+@[^\s@<>()",;:]+\.[^\s@<>()",;:]+$/;

export function isEmailAddress(s: string): boolean {
  return s.length <= 254 && EMAIL.test(s);
}

export const CONSENT_VERSION = /^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/;

/* ── The spec: one small description per key, read by the validator, the merge and the editor ── */

export type Spec =
  | { t: "text"; max: number }
  | { t: "enum"; values: readonly string[] }
  | { t: "bool" }
  | { t: "pattern"; re: RegExp }
  | { t: "email" }
  | { t: "tuple"; of: readonly Spec[] }
  | { t: "list"; of: Spec; min: number; max: number; uniqueCi?: boolean }
  /** An array of distinct values from a closed set that must include `mustInclude`. */
  | { t: "set"; values: readonly string[]; mustInclude: readonly string[] }
  | { t: "object"; fields: Readonly<Record<string, Spec>> };

const text = (max: number): Spec => ({ t: "text", max });
const STEP: Spec = { t: "object", fields: { title: text(60), body: text(200) } };
const SEO: Spec = { t: "object", fields: { title: text(70), description: text(160) } };

export const GAME_PAGE_SPEC: Spec = {
  t: "object",
  fields: {
    blurb: text(280),
    summary: text(160),
    steps: { t: "tuple", of: [STEP, STEP, STEP] },
    adult: text(600),
    moves: { t: "tuple", of: [text(40), text(40), text(40)] },
    levelMoves: { t: "tuple", of: [text(48), text(48), text(48)] },
    heroMedia: { t: "enum", values: HERO_BACKDROPS },
    seo: SEO,
  },
};

export const FOR_SCHOOLS_SPEC: Spec = {
  t: "object",
  fields: {
    hero: {
      t: "object",
      fields: {
        eyebrow: text(60),
        heading: text(80),
        sub: text(240),
        proof: { t: "tuple", of: [text(48), text(48), text(48)] },
        image: { t: "enum", values: SCHOOLS_HERO_IMAGES },
        imageAlt: text(160),
      },
    },
    gets: { t: "object", fields: { label: text(48), heading: text(100), items: { t: "list", of: STEP, min: 1, max: 6 } } },
    rollout: { t: "object", fields: { label: text(48), heading: text(100), steps: { t: "tuple", of: [STEP, STEP, STEP, STEP] } } },
    sees: {
      t: "object",
      fields: { label: text(48), heading: text(100), teacherCaption: text(100), parentCaption: text(100) },
    },
    games: { t: "object", fields: { label: text(48), heading: text(100) } },
    finalHeading: text(80),
    seo: SEO,
  },
};

const REQUIRED_FIELDS_SPEC: Spec = { t: "set", values: ENQUIRY_FIELDS, mustInclude: ENQUIRY_CORE_FIELDS };
const CONSENT_SPEC: Spec = { t: "object", fields: { version: { t: "pattern", re: CONSENT_VERSION }, text: text(600) } };

export const ENQUIRY_FORM_SPEC: Spec = {
  t: "object",
  fields: {
    requiredFields: REQUIRED_FIELDS_SPEC,
    consent: CONSENT_SPEC,
    autoReply: { t: "object", fields: { enabled: { t: "bool" }, subject: text(120), body: text(2000) } },
    alertRecipients: { t: "list", of: { t: "email" }, min: 0, max: 10, uniqueCi: true },
  },
};

/** The enquiry form as the public endpoint serves it. */
export const ENQUIRY_FORM_PUBLIC_SPEC: Spec = {
  t: "object",
  fields: { requiredFields: REQUIRED_FIELDS_SPEC, consent: CONSENT_SPEC },
};

export function specFor(key: SiteKey): Spec {
  if (key === "for-schools") return FOR_SCHOOLS_SPEC;
  if (key === "enquiry-form") return ENQUIRY_FORM_SPEC;
  return GAME_PAGE_SPEC;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Whether `value` passes `spec` exactly — the zod schema's answer, by hand. Objects are strict. */
export function check(spec: Spec, value: unknown): boolean {
  switch (spec.t) {
    case "text":
      return typeof value === "string" && isPlainText(value, spec.max);
    case "enum":
      return typeof value === "string" && spec.values.includes(value);
    case "bool":
      return typeof value === "boolean";
    case "pattern":
      return typeof value === "string" && spec.re.test(value);
    case "email":
      return typeof value === "string" && isEmailAddress(value);
    case "tuple":
      return Array.isArray(value) && value.length === spec.of.length && spec.of.every((s, i) => check(s, value[i]));
    case "list": {
      if (!Array.isArray(value) || value.length < spec.min || value.length > spec.max) return false;
      if (!value.every((v) => check(spec.of, v))) return false;
      if (spec.uniqueCi) {
        const seen = new Set(value.map((v) => String(v).toLowerCase()));
        if (seen.size !== value.length) return false;
      }
      return true;
    }
    case "set":
      return (
        Array.isArray(value) &&
        value.length <= spec.values.length &&
        value.every((v) => typeof v === "string" && spec.values.includes(v)) &&
        new Set(value).size === value.length &&
        spec.mustInclude.every((m) => value.includes(m))
      );
    case "object": {
      if (!isRecord(value)) return false;
      const known = Object.keys(spec.fields);
      if (Object.keys(value).some((k) => !known.includes(k))) return false;
      return known.every((k) => k in value && check(spec.fields[k] as Spec, value[k]));
    }
  }
}

/** The whole-body answer the server gives for a key — the parity tests compare exactly this. */
export function validateBody(key: SiteKey, body: unknown): boolean {
  return check(specFor(key), body);
}

/**
 * `value` where it passes, `fallback` where it does not — recursing into
 * objects and fixed-length tuples, so one bad field costs only that field.
 * A list (variable length) is taken whole or not at all.
 */
export function mergeSpec<T>(spec: Spec, value: unknown, fallback: T): T {
  if (spec.t === "object") {
    if (!isRecord(value) || !isRecord(fallback)) return fallback;
    const out: Record<string, unknown> = {};
    for (const [k, s] of Object.entries(spec.fields)) out[k] = mergeSpec(s, value[k], fallback[k]);
    return out as T;
  }
  if (spec.t === "tuple") {
    if (!Array.isArray(value) || !Array.isArray(fallback) || value.length !== spec.of.length) return fallback;
    return spec.of.map((s, i) => mergeSpec(s, value[i], (fallback as unknown[])[i])) as T;
  }
  return check(spec, value) ? (value as T) : fallback;
}

/* ── The built-in copy, as bodies ──────────────────────────────────────────── */

const DEFAULT_HERO: Record<GamePageKey, HeroBackdropId> = {
  "game.sky-quest": "sky-quest",
  "game.turos-world": "turos-world",
  "game.letter-lagoon": "letter-lagoon",
  "game.bridge-patterns": "bridge-patterns",
};

const GAME_TITLE: Record<GamePageKey, string> = {
  "game.sky-quest": "Sky Quest",
  "game.turos-world": "Turo’s World",
  "game.letter-lagoon": "Letter Lagoon",
  "game.bridge-patterns": "Bridge Patterns",
};

function defaultGamePage(key: GamePageKey): GamePageContent {
  const id = key.slice("game.".length) as GameId;
  const copy = GAME_PAGE_COPY.en[id];
  return {
    blurb: copy.blurb,
    summary: copy.summary,
    steps: [{ ...copy.steps[0] }, { ...copy.steps[1] }, { ...copy.steps[2] }],
    adult: copy.adult,
    moves: [...copy.moves],
    levelMoves: [...copy.levelMoves],
    heroMedia: DEFAULT_HERO[key],
    seo: { title: `${GAME_TITLE[key]} — Kalqy`, description: copy.blurb },
  };
}

function defaultForSchools(): ForSchoolsContent {
  const s = MARKETING_COPY.en.schoolsPage;
  return {
    hero: { eyebrow: s.eyebrow, heading: s.heading, sub: s.sub, proof: [...s.proof], image: "classroom", imageAlt: s.heroAlt },
    gets: { label: s.getsLabel, heading: s.getsHeading, items: s.gets.map((g) => ({ ...g })) },
    rollout: {
      label: s.rolloutLabel,
      heading: s.rolloutHeading,
      steps: [{ ...s.rollout[0] }, { ...s.rollout[1] }, { ...s.rollout[2] }, { ...s.rollout[3] }],
    },
    sees: { label: s.seesLabel, heading: s.seesHeading, teacherCaption: s.teacherCaption, parentCaption: s.parentCaption },
    games: { label: s.gamesLabel, heading: s.gamesHeading },
    finalHeading: s.finalHeading,
    seo: { title: "Kalqy for schools", description: s.sub },
  };
}

/**
 * The enquiry form as it works today: the asterisks on `ContactSalesScreen`
 * (everything but "start by" and the message), its consent line, and no
 * extra recipients (the sales alert then goes to `notify.salesAlertEmail`).
 */
function defaultEnquiryForm(): EnquiryFormContent {
  return {
    requiredFields: ["schoolName", "board", "city", "state", "name", "designation", "email", "phone", "studentRange", "teacherRange"],
    consent: { version: "2026-10", text: "I agree to be contacted by Kalqy about this enquiry" },
    autoReply: {
      enabled: true,
      subject: "We have your enquiry",
      body: "Thank you for telling us about your school. Someone from the Kalqy team will be in touch soon to arrange a demo.",
    },
    alertRecipients: [],
  };
}

/** What a page shows when nothing is published — exactly the copy the bundle ships. */
export function defaultBody<K extends SiteKey>(key: K): SiteContentMap[K] {
  if (key === "for-schools") return defaultForSchools() as SiteContentMap[K];
  if (key === "enquiry-form") return defaultEnquiryForm() as SiteContentMap[K];
  return defaultGamePage(key as GamePageKey) as SiteContentMap[K];
}

/** Published (or draft) JSON over the defaults, field by field. `null` → the defaults. */
export function mergeBody<K extends SiteKey>(key: K, body: unknown): SiteContentMap[K] {
  return mergeSpec(specFor(key), body, defaultBody(key));
}

/* ── Fetching ──────────────────────────────────────────────────────────────── */

/** How long a page waits for published content before keeping its built-in copy for the visit. */
export const SITE_CONTENT_TIMEOUT_MS = 1500;

/**
 * `load`'s answer, or null if it fails or takes longer than `ms`. The load is
 * aborted at the deadline so a slow request does not keep a socket.
 */
export async function loadWithin<T>(load: (signal: AbortSignal) => Promise<T>, ms: number): Promise<T | null> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve(null);
    }, ms);
  });
  try {
    return await Promise.race([load(controller.signal).catch(() => null), deadline]);
  } finally {
    clearTimeout(timer);
  }
}

interface PublishedPayload {
  key: string;
  version: number;
  body: unknown;
}

function isPayload(v: unknown): v is PublishedPayload {
  return isRecord(v) && typeof v.key === "string" && typeof v.version === "number" && "body" in v;
}

/** The fetch, injectable for tests. Resolves the published body or null (404 = nothing published). */
export type PublishedLoader = (key: SiteKey, signal: AbortSignal) => Promise<unknown>;

const httpLoader: PublishedLoader = async (key, signal) => {
  const payload = await request<unknown>(`/site/content/${encodeURIComponent(key)}`, {
    context: "public",
    signal,
    timeoutMs: SITE_CONTENT_TIMEOUT_MS,
  });
  return isPayload(payload) && payload.key === key ? payload.body : null;
};

/** One answer per key per visit — settled values, and the request in flight. */
const settled = new Map<SiteKey, unknown>();
const inflight = new Map<SiteKey, Promise<unknown>>();

/** For tests: forget everything this visit has fetched. */
export function resetSiteContentCache(): void {
  settled.clear();
  inflight.clear();
}

/** The published body for a key, or null — fetched at most once per visit, never slower than 1.5 s. */
export function publishedBody(key: SiteKey, loader: PublishedLoader = httpLoader): Promise<unknown> {
  if (settled.has(key)) return Promise.resolve(settled.get(key) ?? null);
  const running = inflight.get(key);
  if (running) return running;
  const next = loadWithin((signal) => loader(key, signal), SITE_CONTENT_TIMEOUT_MS).then((body) => {
    settled.set(key, body ?? null);
    inflight.delete(key);
    return body ?? null;
  });
  inflight.set(key, next);
  return next;
}

/** A settled body without waiting — so a page revisited this visit renders published content on its first paint. */
export function settledBody(key: SiteKey): unknown {
  return settled.get(key) ?? null;
}

/**
 * The public enquiry-form settings over the built-in ones — for the contact
 * screen (SALES) when it adopts them.
 */
export async function enquiryFormPublic(loader: PublishedLoader = httpLoader): Promise<EnquiryFormPublic> {
  const fallback = defaultBody("enquiry-form");
  const body = await publishedBody("enquiry-form", loader);
  return mergeSpec(ENQUIRY_FORM_PUBLIC_SPEC, body, { requiredFields: fallback.requiredFields, consent: fallback.consent });
}

/* ── Drafts, for an operator ───────────────────────────────────────────────── */

export interface DraftPreview {
  id: string;
  version: number;
}

interface DraftPayload {
  id: string;
  key: string;
  version: number;
  status: string;
  body: unknown;
}

function isDraftPayload(v: unknown): v is DraftPayload {
  return isRecord(v) && typeof v.id === "string" && typeof v.key === "string" && typeof v.version === "number" && "body" in v;
}

/**
 * The `?draft=<id>` on a page's hash, when someone is signed in to read it.
 *
 * ⚠️ THE TOKEN IS ONLY A HINT THAT ASKING IS WORTHWHILE. The server decides:
 * `GET /superadmin/website/versions/:id` is operator-only, so a parent's or a
 * teacher's token gets a 403 and the page shows the published content.
 */
export function draftRequest(hash: string): { id: string; token: string } | null {
  const id = hashParam(hash, "draft");
  if (!id || !/^[A-Za-z0-9_-]{1,64}$/.test(id)) return null;
  const token = getStaffToken();
  return token ? { id, token } : null;
}

async function loadDraft(id: string, token: string, key: SiteKey): Promise<{ body: unknown; preview: DraftPreview } | null> {
  const payload = await loadWithin(
    (signal) => request<unknown>(`/superadmin/website/versions/${encodeURIComponent(id)}`, { context: "user", token, signal }),
    SITE_CONTENT_TIMEOUT_MS * 4,
  );
  if (!isDraftPayload(payload) || payload.key !== key) return null;
  return { body: payload.body, preview: { id: payload.id, version: payload.version } };
}

/**
 * A page's content: its built-in copy on the first paint, then the published
 * version (or, for an operator with `?draft=<id>`, that draft) once it arrives.
 */
export function useSiteContent<K extends PageKey>(key: K): { content: SiteContentMap[K]; draft: DraftPreview | null } {
  const [state, setState] = useState<{ key: K; content: SiteContentMap[K]; draft: DraftPreview | null }>(() => ({
    key,
    content: mergeBody(key, settledBody(key)),
    draft: null,
  }));

  useEffect(() => {
    let alive = true;
    const wanted = typeof window === "undefined" ? null : draftRequest(window.location.hash);
    void (async () => {
      if (wanted) {
        const draft = await loadDraft(wanted.id, wanted.token, key);
        if (!alive) return;
        if (draft) {
          setState({ key, content: mergeBody(key, draft.body), draft: draft.preview });
          return;
        }
      }
      const body = await publishedBody(key);
      if (alive) setState({ key, content: mergeBody(key, body), draft: null });
    })();
    return () => {
      alive = false;
    };
  }, [key]);

  /* A key change renders the new key's built-in copy until its own answer lands, never the old page's words. */
  return state.key === key ? { content: state.content, draft: state.draft } : { content: mergeBody(key, settledBody(key)), draft: null };
}

/* ── SEO ───────────────────────────────────────────────────────────────────── */

/**
 * The page's `<title>` and meta description while it is open, restored on the
 * way out so the app's own title comes back on the next screen.
 */
export function useDocumentSeo(seo: SeoCopy): void {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const previousTitle = document.title;
    let meta = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const created = meta === null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    const previousDescription = meta.content;
    document.title = seo.title;
    meta.content = seo.description;
    return () => {
      document.title = previousTitle;
      if (created) meta.remove();
      else meta.content = previousDescription;
    };
  }, [seo.title, seo.description]);
}

/**
 * A game page's content by game id. Only the four games with a page have a
 * key; any other id (never routed to a page today) gets its built-in copy.
 */
export function useGamePageContent(id: GameId): { content: GamePageContent; draft: DraftPreview | null } {
  const key = gamePageKey(id);
  /* Called unconditionally (rules of hooks); its answer is ignored when the game has no page. */
  const site = useSiteContent(key ?? GAME_PAGE_KEYS[0]);
  if (key) return site;
  const copy = GAME_PAGE_COPY.en[id];
  return { content: { ...copy, heroMedia: "home", seo: { title: "Kalqy", description: copy.blurb } }, draft: null };
}
